/**
 * Sorts an array of items by relevance based on how well their text matches a query
 *
 * Ranking order (highest to lowest priority):
 * 1. Exact match - Full text equals query (e.g., query "dev" matches name "dev")
 * 2. Starts with - Text begins with query (e.g., query "zar" matches "Zara Chen")
 * 3. Word boundary - Query appears at start of any word (e.g., query "chen" matches "Zara Chen")
 * 4. Position - Earlier matches rank higher (e.g., query "ar" in "Zara" ranks higher than in "wizard")
 * 5. Alphabetical - Final tiebreaker for identical relevance
 *
 * @param {Array} items - Array of items to sort
 * @param {string} query - The search query
 * @param {Function|string} textExtractor - Function to extract text from item or property name
 * @param {Object} options - Optional configuration
 * @param {RegExp} options.wordBoundaryPattern - Pattern to split words (default: /\s+/)
 * @returns {Array} Sorted array of items
 */
export function sortByRelevance(items, query, textExtractor, options = {}) {
  const { wordBoundaryPattern = /\s+/ } = options;

  // Handle textExtractor as either a function or a property name
  const getText = typeof textExtractor === 'function' ? textExtractor : (item) => item[textExtractor];

  const queryLower = query.toLowerCase();

  return items.sort((a, b) => {
    const aText = getText(a)?.toLowerCase() || '';
    const bText = getText(b)?.toLowerCase() || '';

    // Priority 1: Exact match
    const aExact = aText === queryLower;
    const bExact = bText === queryLower;
    if (aExact && !bExact) return -1;
    if (bExact && !aExact) return 1;

    // Priority 2: Starts with query
    const aStarts = aText.startsWith(queryLower);
    const bStarts = bText.startsWith(queryLower);
    if (aStarts && !bStarts) return -1;
    if (bStarts && !aStarts) return 1;

    // Priority 3: Word boundary match (query at start of any word)
    const aWordBoundary = aText.split(wordBoundaryPattern).some((word) => word.startsWith(queryLower));
    const bWordBoundary = bText.split(wordBoundaryPattern).some((word) => word.startsWith(queryLower));
    if (aWordBoundary && !bWordBoundary) return -1;
    if (bWordBoundary && !aWordBoundary) return 1;

    // Priority 4: Position of match (earlier is better)
    const aIndex = aText.indexOf(queryLower);
    const bIndex = bText.indexOf(queryLower);
    if (aIndex !== bIndex) return aIndex - bIndex;

    // Finally: Alphabetical order
    return aText.localeCompare(bText);
  });
}
